// The admin console's errors to PostHog error tracking (plan step 2.4; D59), on builds with
// VITE_POSTHOG_KEY. Error tracking only: no pageviews, no replay, no identity, and PostHog's
// cookieless mode, so nothing is stored in the browser. An exception leaves with its type,
// never its message (it can quote what staff were looking at), and URLs keep only their path.
import type { CaptureResult } from 'posthog-js/dist/module.no-external'

/**
 * Any property that holds a URL, by its name: $current_url and $referrer, and the ones PostHog
 * adds on its own ($session_entry_url, $session_entry_referrer, $initial_current_url in $set_once).
 */
const URL_KEY = /url|referrer|href/i

/** A property bag with every URL in it cut before its query and fragment. */
function pathsOnly<B>(bag: B): B {
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return bag
  const out: Record<string, unknown> = { ...(bag as Record<string, unknown>) }
  for (const [key, value] of Object.entries(out))
    if (URL_KEY.test(key) && typeof value === 'string') out[key] = value.split(/[?#]/)[0]
  return out as B
}

type Scrubbable = Pick<CaptureResult, 'event' | 'properties'> &
  Partial<Pick<CaptureResult, '$set' | '$set_once'>>

/** What every event becomes before it leaves the browser. */
export function scrubEvent<T extends Scrubbable>(event: T): T {
  const properties = pathsOnly({ ...event.properties })
  for (const key of ['$set', '$set_once'])
    if (key in properties) properties[key] = pathsOnly(properties[key])
  if (Array.isArray(properties.$exception_list))
    properties.$exception_list = properties.$exception_list.map((e: Record<string, unknown>) => ({
      ...e,
      value: typeof e.type === 'string' ? e.type : 'Error',
    }))
  const out: T = { ...event, properties }
  if (event.$set) out.$set = pathsOnly(event.$set)
  if (event.$set_once) out.$set_once = pathsOnly(event.$set_once)
  return out
}

/** Starts error tracking; a no-op, with no PostHog code in the bundle, on a build without a key. */
export async function startErrorTracking(): Promise<void> {
  // A build-time constant: built without a key, the PostHog chunk is never emitted.
  if (!import.meta.env.VITE_POSTHOG_KEY) return
  const posthog = (await import('posthog-js/dist/module.no-external')).default
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com',
    defaults: '2026-08-30',
    cookieless_mode: 'always',
    person_profiles: 'never',
    disable_external_dependency_loading: true,
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    rageclick: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_performance: false,
    capture_exceptions: true,
    disable_session_recording: true,
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_web_experiments: true,
    advanced_disable_feature_flags: true,
    debug: false,
    // Only exceptions leave; anything else PostHog might send is dropped here.
    before_send: (event) => (event?.event === '$exception' ? scrubEvent(event) : null),
  })
}
